import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { adminAPI } from '../../services/api';
import {
  Users, Files, ShoppingBag, LifeBuoy, Settings, CreditCard,
  Search, ChevronRight, ChevronDown, Loader2, Check, X, Trash2, Key,
  Package, Truck, FileText, Globe, Download, Eye, Edit,
  DollarSign, Zap, RefreshCw, Database, Upload, AlertTriangle,
  MapPin, Phone, Mail, Calendar, Hash, Shield, Send, Clock,
  Copy, UserCheck, UsersIcon, MessageCircle, Power, BarChart3,
  TrendingUp, TrendingDown, Activity, Brain, Wrench
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const TABS = [
  { id: 'files', label: 'Dateien', icon: Files },
  { id: 'users', label: 'Benutzer', icon: Users },
  { id: 'orders', label: 'Bestellungen', icon: ShoppingBag },
  { id: 'accounting', label: 'Buchhaltung', icon: FileText },
  { id: 'credits', label: 'Credit-Verlauf', icon: Activity },
  { id: 'analytics', label: 'Qualität & KPIs', icon: BarChart3 },
  { id: 'notifications', label: 'Benachrichtigungen', icon: Mail },
  { id: 'support', label: 'Support', icon: LifeBuoy },
  { id: 'pricing', label: 'Preise & Pakete', icon: CreditCard },
  { id: 'scraper', label: 'Fahrzeug-DB', icon: Database },
];

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'files');

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'operator') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-page">
        <h1 className="font-heading text-2xl font-bold text-white">Admin Panel</h1>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-[#EC2229] text-white' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'files' && <FilesTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'orders' && <OrdersTab />}
        {activeTab === 'accounting' && <AccountingTab />}
        {activeTab === 'credits' && <CreditHistoryTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'support' && <SupportTab />}
        {activeTab === 'pricing' && <PricingTab />}
        {activeTab === 'scraper' && <ScraperTab />}
      </div>
    </DashboardLayout>
  );
};

// ======================== FILES TAB ========================
const FilesTab = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  const fetchFiles = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.getFiles(params);
      setFiles(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400',
    in_progress: 'bg-blue-500/10 text-blue-400',
    completed: 'bg-emerald-500/10 text-emerald-400',
    revision_requested: 'bg-orange-500/10 text-orange-400',
  };

  return (
    <div className="glass-card p-6" data-testid="files-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Dateien ({files.length})</h2>
        <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="input-field w-48"><SelectValue placeholder="Alle Status" /></SelectTrigger>
          <SelectContent className="bg-[#23272F] border-white/10">
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="pending">Wartend</SelectItem>
            <SelectItem value="in_progress">In Bearbeitung</SelectItem>
            <SelectItem value="completed">Fertig</SelectItem>
            <SelectItem value="revision_requested">Revision</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/[0.06]">
              <th className="text-left py-2 px-3 text-xs text-slate-500">Ticket</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Fahrzeug</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Tool</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Credits</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Status</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Datum</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500"></th>
            </tr></thead>
            <tbody>
              {files.map(f => (
                <tr key={f.id} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors" data-testid={`file-row-${f.id}`}>
                  <td className="py-2.5 px-3 text-[#EC2229] font-mono text-xs cursor-pointer" onClick={() => navigate(`/files/${f.id}`)}>{f.ticket_id}</td>
                  <td className="py-2.5 px-3 text-white cursor-pointer" onClick={() => navigate(`/files/${f.id}`)}>{f.vehicle_brand} {f.vehicle_model}</td>
                  <td className="py-2.5 px-3 text-slate-400 cursor-pointer" onClick={() => navigate(`/files/${f.id}`)}>{f.tool_type}</td>
                  <td className="py-2.5 px-3 text-white font-mono cursor-pointer" onClick={() => navigate(`/files/${f.id}`)}>{f.credit_cost}</td>
                  <td className="py-2.5 px-3 cursor-pointer" onClick={() => navigate(`/files/${f.id}`)}><span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[f.status] || ''}`}>{f.status}</span></td>
                  <td className="py-2.5 px-3 text-slate-500 text-xs cursor-pointer" onClick={() => navigate(`/files/${f.id}`)}>{new Date(f.created_at).toLocaleDateString('de-DE')}</td>
                  <td className="py-2.5 px-3">
                    <button onClick={async (e) => { e.stopPropagation(); if(confirm('Datei wirklich löschen?')) { await adminAPI.deleteFile(f.id); fetchFiles(); } }} className="text-slate-600 hover:text-red-400 transition-colors" data-testid={`delete-file-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {files.length === 0 && <p className="text-slate-500 text-center py-8">Keine Dateien</p>}
        </div>
      )}
    </div>
  );
};

// ======================== USERS TAB ========================
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showResetPw, setShowResetPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [creditAdjust, setCreditAdjust] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [setBalance, setSetBalance] = useState(null);
  const [newBalance, setNewBalance] = useState('');
  const [setBalanceReason, setSetBalanceReason] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers({ limit: 200 });
      setUsers(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDeleteUser = async (userId) => {
    try {
      await adminAPI.deleteUser(userId);
      setShowDeleteConfirm(null);
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleResetPassword = async (userId) => {
    if (newPassword.length < 6) { alert('Passwort muss mindestens 6 Zeichen lang sein'); return; }
    try {
      await adminAPI.resetUserPassword(userId, newPassword);
      setShowResetPw(null);
      setNewPassword('');
      alert('Passwort zurückgesetzt');
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleCreditAdjust = async () => {
    if (!creditAmount || !creditReason) return;
    try {
      await adminAPI.adjustCredits(creditAdjust.id, parseFloat(creditAmount), creditReason);
      setCreditAdjust(null);
      setCreditAmount('');
      setCreditReason('');
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleSetBalance = async () => {
    if (newBalance === '' || !setBalanceReason) return;
    try {
      await adminAPI.correctBalance(setBalance.id, parseFloat(newBalance), setBalanceReason);
      setSetBalance(null);
      setNewBalance('');
      setSetBalanceReason('');
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleToggleActive = async (u) => {
    await adminAPI.updateUser(u.id, { is_active: !u.is_active });
    fetchUsers();
  };

  const filteredUsers = users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="glass-card p-6" data-testid="users-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Benutzer ({users.length})</h2>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..." className="input-field pl-10" data-testid="user-search" />
        </div>
      </div>

      {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div> : (
        <div className="space-y-2">
          {filteredUsers.map(u => (
            <div key={u.id} className="bg-white/[0.03] rounded-lg border border-white/[0.06] hover:border-white/[0.12] transition-all overflow-hidden" data-testid={`user-row-${u.id}`}>
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={(e) => {
                  // ignore clicks on action buttons
                  if (e.target.closest('button')) return;
                  setExpandedUserId(expandedUserId === u.id ? null : u.id);
                }}
                data-testid={`user-row-toggle-${u.id}`}
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${u.role === 'admin' ? 'bg-[#EC2229]/20' : 'bg-blue-500/20'}`}>
                    <span className={`font-bold ${u.role === 'admin' ? 'text-[#EC2229]' : 'text-blue-400'}`}>{u.name?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium truncate">{u.name}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-[#EC2229]/10 text-[#EC2229]' : 'bg-blue-500/10 text-blue-400'}`}>{u.role}</span>
                      {!u.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">Deaktiviert</span>}
                      {u.ai_evaluation_disabled && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-400">KI-Aus</span>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right mr-4">
                    <p className="text-white font-mono font-bold">{(u.balance || 0).toFixed(1)}</p>
                    <p className="text-[10px] text-slate-500">Credits</p>
                  </div>

                  {/* Credit Adjust (+/- delta) */}
                  <Button size="sm" variant="ghost" onClick={() => setCreditAdjust(u)} className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" title="Credits anpassen (+/-)" data-testid={`credit-btn-${u.id}`}>
                    <DollarSign className="w-4 h-4" />
                  </Button>

                  {/* Set Balance (exakter Wert) */}
                  <Button size="sm" variant="ghost" onClick={() => { setSetBalance(u); setNewBalance((u.balance || 0).toFixed(1)); setSetBalanceReason(''); }} className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" title="Guthaben exakt setzen (mit Audit-Log)" data-testid={`set-balance-btn-${u.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>

                  {/* Reset Password */}
                  <Button size="sm" variant="ghost" onClick={() => { setShowResetPw(u); setNewPassword(''); }} className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" title="Passwort ändern" data-testid={`reset-pw-btn-${u.id}`}>
                    <Key className="w-4 h-4" />
                  </Button>

                  {/* Toggle Active */}
                  <Button size="sm" variant="ghost" onClick={() => handleToggleActive(u)} className={u.is_active ? "text-slate-400 hover:text-red-400 hover:bg-red-500/10" : "text-emerald-400 hover:bg-emerald-500/10"} title={u.is_active ? 'Deaktivieren' : 'Aktivieren'} data-testid={`toggle-active-${u.id}`}>
                    {u.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </Button>

                  {/* Toggle AI Evaluation */}
                  <Button size="sm" variant="ghost" onClick={async () => {
                    await adminAPI.updateUser(u.id, { ai_evaluation_disabled: !u.ai_evaluation_disabled });
                    fetchUsers();
                  }} className={u.ai_evaluation_disabled ? "text-red-400 hover:text-purple-400 hover:bg-purple-500/10" : "text-purple-400 hover:text-red-400 hover:bg-red-500/10"} title={u.ai_evaluation_disabled ? 'KI-Bewertung aktivieren' : 'KI-Bewertung deaktivieren'} data-testid={`toggle-ai-${u.id}`}>
                    <Brain className="w-4 h-4" />
                  </Button>

                  {/* Delete User */}
                  {u.role !== 'admin' && (
                    <Button size="sm" variant="ghost" onClick={() => setShowDeleteConfirm(u)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" title="Löschen" data-testid={`delete-btn-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}

                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ml-1 ${expandedUserId === u.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Detail Panel */}
              {expandedUserId === u.id && (
                <div className="border-t border-white/[0.06] bg-black/20 p-5" data-testid={`user-details-${u.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
                    {/* Kontakt */}
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Mail className="w-3 h-3" /> Kontakt</h4>
                      <dl className="space-y-1.5">
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">E-Mail</dt><dd className="text-slate-200 break-all">{u.email}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Telefon</dt><dd className="text-slate-200">{u.phone || '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Firma</dt><dd className="text-slate-200">{u.company || '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Rolle</dt><dd className="text-slate-200">{u.role}</dd></div>
                      </dl>
                    </div>
                    {/* Adresse */}
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Adresse</h4>
                      <dl className="space-y-1.5">
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Straße</dt><dd className="text-slate-200">{u.address_street || '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">PLZ + Ort</dt><dd className="text-slate-200">{[u.address_zip, u.address_city].filter(Boolean).join(' ') || '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Land</dt><dd className="text-slate-200">{u.address_country || '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">USt-IdNr.</dt><dd className="text-slate-200 font-mono text-xs">{u.vat_id || '—'}</dd></div>
                      </dl>
                    </div>
                    {/* Konto-Info */}
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Settings className="w-3 h-3" /> Konto</h4>
                      <dl className="space-y-1.5">
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Sprache</dt><dd className="text-slate-200">{u.locale === 'en' ? 'English' : u.locale === 'de' ? 'Deutsch' : (u.locale || '—')}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Zeitzone</dt><dd className="text-slate-200">{u.timezone || '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Registriert</dt><dd className="text-slate-200">{u.created_at ? new Date(u.created_at).toLocaleString('de-DE') : '—'}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Status</dt><dd>{u.is_active ? <span className="text-emerald-400">Aktiv</span> : <span className="text-red-400">Deaktiviert</span>}</dd></div>
                        <div className="flex flex-col"><dt className="text-[10px] text-slate-600">Guthaben</dt><dd className={`font-mono font-bold ${(u.balance || 0) >= 0 ? 'text-white' : 'text-red-400'}`}>{(u.balance || 0).toFixed(1)} Credits</dd></div>
                      </dl>
                    </div>
                  </div>

                  {/* Tuning Tools */}
                  {(u.tuning_tools?.length || u.tuning_tools_other) && (
                    <div className="mt-5 pt-4 border-t border-white/[0.04]">
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Wrench className="w-3 h-3" /> Tuning-Tools</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {(u.tuning_tools || []).map((tool, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#EC2229]/10 text-[#EC2229]">{tool}</span>
                        ))}
                        {u.tuning_tools_other && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-300">{u.tuning_tools_other}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {filteredUsers.length === 0 && <p className="text-slate-500 text-center py-8">Keine Benutzer gefunden</p>}
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPw && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass-card p-6 w-full max-w-md mx-4" data-testid="reset-pw-modal">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2"><Key className="w-5 h-5 text-amber-400" /> Passwort ändern</h3>
            <p className="text-sm text-slate-400 mb-4">Für: <span className="text-white">{showResetPw.name}</span> ({showResetPw.email})</p>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Neues Passwort (min. 6 Zeichen)" className="input-field mb-4" data-testid="new-password-input" />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowResetPw(null)} className="btn-secondary flex-1">Abbrechen</Button>
              <Button onClick={() => handleResetPassword(showResetPw.id)} className="btn-primary flex-1" data-testid="confirm-reset-pw">Passwort setzen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass-card p-6 w-full max-w-md mx-4" data-testid="delete-user-modal">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Benutzer löschen</h3>
            <p className="text-sm text-slate-400 mb-1">Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?</p>
            <p className="text-white font-medium mb-4">{showDeleteConfirm.name} ({showDeleteConfirm.email})</p>
            <p className="text-xs text-red-400/60 mb-4">Alle Daten (Transaktionen, Tickets, Benachrichtigungen) werden unwiderruflich gelöscht.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)} className="btn-secondary flex-1">Abbrechen</Button>
              <Button onClick={() => handleDeleteUser(showDeleteConfirm.id)} className="bg-red-600 hover:bg-red-700 text-white flex-1" data-testid="confirm-delete-user">Endgültig löschen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Adjust Modal */}
      {creditAdjust && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass-card p-6 w-full max-w-md mx-4" data-testid="credit-adjust-modal">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> Credits anpassen</h3>
            <p className="text-sm text-slate-400 mb-4">{creditAdjust.name} — Aktuell: <span className="text-white font-mono">{(creditAdjust.balance || 0).toFixed(1)} Credits</span></p>
            <Input type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} placeholder="Betrag (z.B. 10 oder -5)" className="input-field mb-3" data-testid="credit-amount-input" />
            <Input value={creditReason} onChange={e => setCreditReason(e.target.value)} placeholder="Grund (z.B. Gutschrift, Korrektur)" className="input-field mb-4" data-testid="credit-reason-input" />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCreditAdjust(null)} className="btn-secondary flex-1">Abbrechen</Button>
              <Button onClick={handleCreditAdjust} className="btn-primary flex-1" data-testid="confirm-credit-adjust">Anwenden</Button>
            </div>
          </div>
        </div>
      )}

      {/* Set Balance Modal (exakter Wert + Audit-Log) */}
      {setBalance && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass-card p-6 w-full max-w-md mx-4" data-testid="set-balance-modal">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2"><Edit className="w-5 h-5 text-cyan-400" /> Guthaben exakt setzen</h3>
            <p className="text-sm text-slate-400 mb-4">{setBalance.name} ({setBalance.email}) — Aktuell: <span className="text-white font-mono">{(setBalance.balance || 0).toFixed(1)} Credits</span></p>
            <Label className="text-xs text-slate-500">Neues Guthaben (Credits)</Label>
            <Input type="number" step="0.1" value={newBalance} onChange={e => setNewBalance(e.target.value)} placeholder="z.B. 30.0" className="input-field mb-3" data-testid="new-balance-input" />
            <Label className="text-xs text-slate-500">Grund (Audit-Log)</Label>
            <Textarea value={setBalanceReason} onChange={e => setSetBalanceReason(e.target.value)} placeholder="z.B. Wiederherstellung nach fehlerhaftem Backfill" className="input-field mb-4" rows={2} data-testid="set-balance-reason" />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setSetBalance(null)} className="btn-secondary flex-1">Abbrechen</Button>
              <Button onClick={handleSetBalance} className="btn-primary flex-1" data-testid="confirm-set-balance" disabled={newBalance === '' || !setBalanceReason}>Guthaben setzen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ======================== ORDERS TAB ========================
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('DHL');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.getShopOrders(params);
      setOrders(res.data?.orders || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openOrder = async (orderId) => {
    setLoadingDetail(true);
    try {
      const res = await adminAPI.getShopOrder(orderId);
      setSelectedOrder(res.data);
      setTrackingNumber(res.data.tracking_number || '');
      setCarrier(res.data.carrier || 'DHL');
    } catch (err) { console.error(err); }
    finally { setLoadingDetail(false); }
  };

  const handleStatusChange = async (status) => {
    if (!selectedOrder) return;
    try {
      await adminAPI.updateShopOrderStatus(selectedOrder.id, status);
      const res = await adminAPI.getShopOrder(selectedOrder.id);
      setSelectedOrder(res.data);
      fetchOrders();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleSendTracking = async () => {
    if (!trackingNumber || !selectedOrder) return;
    try {
      await adminAPI.updateOrderTracking(selectedOrder.id, trackingNumber, carrier);
      const res = await adminAPI.getShopOrder(selectedOrder.id);
      setSelectedOrder(res.data);
      fetchOrders();
      alert('Sendungsverfolgung gesendet');
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleCreateInvoice = async () => {
    if (!selectedOrder) return;
    try {
      const res = await adminAPI.createOrderInvoice(selectedOrder.id);
      const updated = await adminAPI.getShopOrder(selectedOrder.id);
      setSelectedOrder(updated.data);
      alert(res.data.message);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const statusColors = {
    pending: 'bg-amber-500/10 text-amber-400',
    awaiting_payment: 'bg-amber-500/10 text-amber-400',
    paid: 'bg-blue-500/10 text-blue-400',
    shipped: 'bg-purple-500/10 text-purple-400',
    completed: 'bg-emerald-500/10 text-emerald-400',
    cancelled: 'bg-red-500/10 text-red-400',
  };
  const statusLabels = {
    pending: 'Ausstehend', awaiting_payment: 'Warte auf Zahlung', paid: 'Bezahlt',
    shipped: 'Versendet', completed: 'Abgeschlossen', cancelled: 'Storniert',
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="orders-tab">
      {/* Order List */}
      <div className={`${selectedOrder ? 'lg:col-span-1' : 'lg:col-span-3'} glass-card p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Bestellungen ({orders.length})</h2>
          <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="input-field w-40"><SelectValue placeholder="Alle" /></SelectTrigger>
            <SelectContent className="bg-[#23272F] border-white/10">
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="pending">Ausstehend</SelectItem>
              <SelectItem value="awaiting_payment">Warte auf Zahlung</SelectItem>
              <SelectItem value="shipped">Versendet</SelectItem>
              <SelectItem value="completed">Abgeschlossen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div> : (
          <div className="space-y-2">
            {orders.map(o => (
              <button key={o.id} onClick={() => openOrder(o.id)} className={`w-full text-left p-3 rounded-lg border transition-all ${selectedOrder?.id === o.id ? 'bg-[#EC2229]/10 border-[#EC2229]/30' : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'}`} data-testid={`order-row-${o.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-[#EC2229]">{o.order_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColors[o.status] || ''}`}>{statusLabels[o.status] || o.status}</span>
                </div>
                <p className="text-sm text-white">{o.product_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-500">{o.user_name}</span>
                  <span className="text-xs text-white font-mono">{o.price_gross?.toFixed(2)} EUR</span>
                </div>
              </button>
            ))}
            {orders.length === 0 && <p className="text-slate-500 text-center py-8">Keine Bestellungen</p>}
          </div>
        )}
      </div>

      {/* Order Detail */}
      {selectedOrder && (
        <div className="lg:col-span-2 space-y-4" data-testid="order-detail">
          {loadingDetail ? <div className="glass-card p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div> : (
            <>
              {/* Order Header */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white font-mono">{selectedOrder.order_number}</h3>
                    <p className="text-sm text-slate-400">{new Date(selectedOrder.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedOrder.status] || ''}`}>{statusLabels[selectedOrder.status] || selectedOrder.status}</span>
                </div>

                {/* Product */}
                <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.06] mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Package className="w-5 h-5 text-[#EC2229]" />
                    <span className="text-white font-medium">{selectedOrder.product_name}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><p className="text-slate-500">Netto</p><p className="text-white font-mono">{selectedOrder.price_net?.toFixed(2)} EUR</p></div>
                    <div><p className="text-slate-500">MwSt. ({selectedOrder.vat_rate}%)</p><p className="text-white font-mono">{selectedOrder.vat_amount?.toFixed(2)} EUR</p></div>
                    <div><p className="text-slate-500">Gesamt</p><p className="text-[#EC2229] font-mono font-bold">{selectedOrder.price_gross?.toFixed(2)} EUR</p></div>
                  </div>
                </div>

                {/* Customer + Shipping */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.06]">
                    <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Kunde</h4>
                    <p className="text-white font-medium">{selectedOrder.user_name}</p>
                    <p className="text-sm text-slate-400 flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {selectedOrder.user_email}</p>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.06]">
                    <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Versandadresse</h4>
                    {selectedOrder.shipping ? (
                      <>
                        <p className="text-white font-medium">{selectedOrder.shipping.name}</p>
                        <p className="text-sm text-slate-400">{selectedOrder.shipping.street}</p>
                        <p className="text-sm text-slate-400">{selectedOrder.shipping.zip} {selectedOrder.shipping.city}</p>
                        <p className="text-sm text-slate-400">{selectedOrder.shipping.country}</p>
                        {selectedOrder.shipping.phone && <p className="text-sm text-slate-400 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {selectedOrder.shipping.phone}</p>}
                      </>
                    ) : <p className="text-slate-500">Keine Versanddaten</p>}
                  </div>
                </div>

                {selectedOrder.notes && (
                  <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg">
                    <p className="text-xs text-amber-400 mb-1">Kundennotiz:</p>
                    <p className="text-sm text-slate-300">{selectedOrder.notes}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="glass-card p-6">
                <h4 className="text-sm font-semibold text-white mb-4">Aktionen</h4>

                {/* Status Change */}
                <div className="mb-4">
                  <Label className="label-text mb-2">Status ändern</Label>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'awaiting_payment', 'paid', 'shipped', 'completed', 'cancelled'].map(s => (
                      <button key={s} onClick={() => handleStatusChange(s)} disabled={selectedOrder.status === s} className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${selectedOrder.status === s ? statusColors[s] + ' ring-1 ring-white/20' : 'bg-white/5 border-white/10 text-slate-500 hover:text-white'}`} data-testid={`order-status-${s}`}>
                        {statusLabels[s]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tracking */}
                <div className="mb-4 pt-4 border-t border-white/[0.06]">
                  <Label className="label-text mb-2 flex items-center gap-1"><Truck className="w-3 h-3" /> Sendungsverfolgung</Label>
                  <div className="flex gap-2">
                    <Select value={carrier} onValueChange={setCarrier}>
                      <SelectTrigger className="input-field w-28"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#23272F] border-white/10">
                        <SelectItem value="DHL">DHL</SelectItem>
                        <SelectItem value="DPD">DPD</SelectItem>
                        <SelectItem value="GLS">GLS</SelectItem>
                        <SelectItem value="Hermes">Hermes</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Sendungsnummer" className="input-field flex-1" data-testid="tracking-input" />
                    <Button onClick={handleSendTracking} disabled={!trackingNumber} className="btn-primary" data-testid="send-tracking-btn">
                      <Truck className="w-4 h-4 mr-2" /> Senden
                    </Button>
                  </div>
                  {selectedOrder.tracking_number && (
                    <div className="mt-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg text-xs">
                      <span className="text-purple-400">{selectedOrder.carrier || 'DHL'}: {selectedOrder.tracking_number}</span>
                    </div>
                  )}
                </div>

                {/* Invoice */}
                <div className="pt-4 border-t border-white/[0.06]">
                  <Label className="label-text mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Rechnung</Label>
                  {selectedOrder.invoice_number ? (
                    <div className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-400">Rechnung {selectedOrder.invoice_number} erstellt</span>
                    </div>
                  ) : (
                    <Button onClick={handleCreateInvoice} className="btn-primary" data-testid="create-invoice-btn">
                      <FileText className="w-4 h-4 mr-2" /> Rechnung erstellen
                    </Button>
                  )}
                </div>

                {/* Delete */}
                <div className="pt-4 border-t border-white/[0.06]">
                  <button onClick={async () => { if(confirm('Bestellung wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) { await adminAPI.deleteOrder(selectedOrder.id); setSelectedOrder(null); fetchOrders(); } }} className="text-xs text-red-400/60 hover:text-red-400 transition-colors flex items-center gap-1" data-testid="delete-order-btn">
                    <Trash2 className="w-3 h-3" /> Bestellung löschen
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};


// ======================== NOTIFICATIONS TAB ========================
const NotificationsTab = () => {
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [recipientCounts, setRecipientCounts] = useState({ all: 0, active: 0 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [chatToggling, setChatToggling] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ mollie: { configured: false, enabled: false }, paypal: { configured: false, enabled: false } });
  const [paymentToggling, setPaymentToggling] = useState('');

  // Form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientType, setRecipientType] = useState('active');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [tplRes, histRes, countRes, usersRes, chatRes, payRes] = await Promise.all([
        adminAPI.getEmailTemplates(),
        adminAPI.getNotificationHistory(),
        adminAPI.getRecipientCounts(),
        adminAPI.getUsers({ limit: 200 }),
        adminAPI.getChatStatus(),
        adminAPI.getPaymentMethodsStatus(),
      ]);
      setTemplates(tplRes.data?.predefined || []);
      setCustomTemplates(tplRes.data?.custom || []);
      setHistory(histRes.data || []);
      setRecipientCounts(countRes.data || { all: 0, active: 0 });
      setUsers((usersRes.data || []).filter(u => u.role !== 'admin'));
      setChatEnabled(chatRes.data?.enabled !== false);
      setPaymentMethods(payRes.data || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChatToggle = async () => {
    setChatToggling(true);
    try {
      const res = await adminAPI.toggleChat(!chatEnabled);
      setChatEnabled(res.data?.enabled);
    } catch (err) { console.error(err); }
    finally { setChatToggling(false); }
  };

  const handlePaymentToggle = async (method) => {
    setPaymentToggling(method);
    const current = paymentMethods[method]?.enabled;
    try {
      await adminAPI.togglePaymentMethod(method, !current);
      setPaymentMethods(prev => ({ ...prev, [method]: { ...prev[method], enabled: !current } }));
    } catch (err) { console.error(err); }
    finally { setPaymentToggling(''); }
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  const getRecipientCount = () => {
    if (recipientType === 'all') return recipientCounts.all;
    if (recipientType === 'active') return recipientCounts.active;
    return selectedUserIds.length;
  };

  const getRecipientsParam = () => {
    if (recipientType === 'all') return 'all';
    if (recipientType === 'active') return 'active';
    return selectedUserIds.join(',');
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { alert('Betreff und Nachricht sind erforderlich'); return; }
    if (recipientType === 'selected' && selectedUserIds.length === 0) { alert('Bitte wählen Sie mindestens einen Empfänger'); return; }
    if (!confirm(`Benachrichtigung an ${getRecipientCount()} Empfänger senden?`)) return;

    setSending(true);
    try {
      const res = await adminAPI.sendNotification(subject, body, getRecipientsParam());
      alert(res.data.message);
      setSubject('');
      setBody('');
      setSelectedUserIds([]);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler beim Senden'); }
    finally { setSending(false); }
  };

  const toggleUser = (userId) => {
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const filteredUsers = users.filter(u =>
    !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>;

  return (
    <div className="space-y-6" data-testid="notifications-tab">
      {/* Chat Toggle */}
      <div className="glass-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${chatEnabled ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
            <MessageCircle className={`w-5 h-5 ${chatEnabled ? 'text-emerald-400' : 'text-red-400'}`} />
          </div>
          <div>
            <p className="text-sm text-white font-medium">Live-Chat System</p>
            <p className="text-xs text-slate-500">{chatEnabled ? 'Chat ist aktiv - Kunden und Team können chatten' : 'Chat ist deaktiviert - Chat-Blase ist ausgeblendet'}</p>
          </div>
        </div>
        <button
          onClick={handleChatToggle}
          disabled={chatToggling}
          className={`relative w-12 h-6 rounded-full transition-colors ${chatEnabled ? 'bg-emerald-500' : 'bg-slate-600'}`}
          data-testid="chat-toggle"
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${chatEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {/* Payment Method Toggles */}
      <div className="glass-card p-4 space-y-3">
        <p className="text-sm text-white font-medium flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#EC2229]" /> Zahlungsmethoden</p>
        {Object.entries(paymentMethods).map(([method, info]) => (
          <div key={method} className="flex items-center justify-between pl-2">
            <div>
              <p className="text-sm text-white">{method === 'mollie' ? 'Mollie' : 'PayPal'}</p>
              <p className="text-[10px] text-slate-500">{info.configured ? (info.enabled ? 'Aktiv' : 'Deaktiviert') : 'Nicht konfiguriert'}</p>
            </div>
            <button
              onClick={() => handlePaymentToggle(method)}
              disabled={!info.configured || paymentToggling === method}
              className={`relative w-12 h-6 rounded-full transition-colors ${info.enabled && info.configured ? 'bg-emerald-500' : 'bg-slate-600'} ${!info.configured ? 'opacity-40' : ''}`}
              data-testid={`payment-toggle-${method}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${info.enabled && info.configured ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Templates */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#EC2229]" /> Vorlage wählen
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {templates.map(tpl => (
                <button key={tpl.id} onClick={() => applyTemplate(tpl)} className="p-3 bg-white/[0.03] rounded-lg border border-white/[0.06] hover:border-[#EC2229]/40 hover:bg-[#EC2229]/5 transition-all text-left" data-testid={`template-${tpl.id}`}>
                  <p className="text-sm text-white font-medium">{tpl.name}</p>
                </button>
              ))}
            </div>
            {customTemplates.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/[0.06]">
                <p className="text-xs text-slate-500 mb-2">Gespeicherte Vorlagen</p>
                <div className="flex flex-wrap gap-2">
                  {customTemplates.map(tpl => (
                    <div key={tpl.id} className="flex items-center gap-1">
                      <button onClick={() => applyTemplate(tpl)} className="text-xs px-3 py-1.5 bg-white/5 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-all">
                        {tpl.name}
                      </button>
                      <button onClick={async () => { await adminAPI.deleteEmailTemplate(tpl.id); fetchData(); }} className="text-slate-600 hover:text-red-400 p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Compose */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#EC2229]" /> Nachricht verfassen
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="label-text">Betreff</Label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="E-Mail Betreff..." className="input-field" data-testid="notification-subject" />
              </div>
              <div>
                <Label className="label-text">Nachricht <span className="text-slate-600 font-normal">({'{name}'} = Kundenname)</span></Label>
                <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="E-Mail Nachricht..." className="input-field min-h-[200px] font-mono text-sm" data-testid="notification-body" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowPreview(true)} variant="outline" className="btn-secondary" disabled={!subject || !body} data-testid="preview-btn">
                  <Eye className="w-4 h-4 mr-2" /> Vorschau
                </Button>
                <Button onClick={handleSend} className="btn-primary" disabled={sending || !subject || !body} data-testid="send-notification-btn">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  An {getRecipientCount()} Empfänger senden
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: Recipients + History */}
        <div className="space-y-6">
          {/* Recipient Selection */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#EC2229]" /> Empfänger
            </h2>
            <div className="space-y-2 mb-4">
              <button onClick={() => setRecipientType('active')} className={`w-full text-left p-3 rounded-lg border transition-all flex items-center justify-between ${recipientType === 'active' ? 'bg-[#EC2229]/10 border-[#EC2229]/30' : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'}`} data-testid="recipient-active">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-white">Aktive Kunden</span>
                </div>
                <span className="text-xs font-mono text-slate-400">{recipientCounts.active}</span>
              </button>
              <button onClick={() => setRecipientType('all')} className={`w-full text-left p-3 rounded-lg border transition-all flex items-center justify-between ${recipientType === 'all' ? 'bg-[#EC2229]/10 border-[#EC2229]/30' : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'}`} data-testid="recipient-all">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-white">Alle Kunden</span>
                </div>
                <span className="text-xs font-mono text-slate-400">{recipientCounts.all}</span>
              </button>
              <button onClick={() => setRecipientType('selected')} className={`w-full text-left p-3 rounded-lg border transition-all flex items-center justify-between ${recipientType === 'selected' ? 'bg-[#EC2229]/10 border-[#EC2229]/30' : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'}`} data-testid="recipient-selected">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-white">Einzelne Benutzer</span>
                </div>
                <span className="text-xs font-mono text-slate-400">{selectedUserIds.length}</span>
              </button>
            </div>

            {recipientType === 'selected' && (
              <div>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                  <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Suchen..." className="input-field pl-8 text-xs" data-testid="recipient-search" />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredUsers.map(u => (
                    <button key={u.id} onClick={() => toggleUser(u.id)} className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 transition-all ${selectedUserIds.includes(u.id) ? 'bg-[#EC2229]/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`} data-testid={`recipient-user-${u.id}`}>
                      <div className={`w-3 h-3 rounded-sm border flex items-center justify-center ${selectedUserIds.includes(u.id) ? 'bg-[#EC2229] border-[#EC2229]' : 'border-slate-600'}`}>
                        {selectedUserIds.includes(u.id) && <Check className="w-2 h-2 text-white" />}
                      </div>
                      <span className="truncate">{u.name}</span>
                      <span className="text-slate-600 truncate ml-auto">{u.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* History Toggle */}
          <div className="glass-card p-6">
            <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between" data-testid="toggle-history">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#EC2229]" /> Versand-Historie
              </h2>
              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
            </button>
            {showHistory && (
              <div className="mt-4 space-y-2">
                {history.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Noch keine Benachrichtigungen gesendet</p>}
                {history.map(h => (
                  <div key={h.id} className="bg-white/[0.03] rounded-lg p-3 border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-white font-medium truncate">{h.subject}</p>
                      <span className="text-[10px] text-slate-500">{new Date(h.created_at).toLocaleDateString('de-DE')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-emerald-400">{h.sent_count} gesendet</span>
                      {h.failed_count > 0 && <span className="text-red-400">{h.failed_count} fehlgeschlagen</span>}
                      <span className="text-slate-500 ml-auto">{h.sent_by}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass-card p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" data-testid="preview-modal">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Eye className="w-5 h-5 text-[#EC2229]" /> Vorschau</h3>
            <div className="bg-white/[0.03] rounded-lg p-6 border border-white/[0.06] mb-4">
              <div className="mb-4 pb-4 border-b border-white/[0.06]">
                <p className="text-xs text-slate-500">Betreff</p>
                <p className="text-white font-medium">{subject}</p>
              </div>
              <div className="mb-4 pb-4 border-b border-white/[0.06]">
                <p className="text-xs text-slate-500 mb-1">Empfänger</p>
                <p className="text-sm text-slate-300">{recipientType === 'all' ? `Alle Kunden (${recipientCounts.all})` : recipientType === 'active' ? `Aktive Kunden (${recipientCounts.active})` : `${selectedUserIds.length} ausgewählte Benutzer`}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-2">Nachricht (Beispiel)</p>
                <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans">{body.replace('{name}', 'Max Mustermann')}</pre>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="btn-secondary flex-1">Schließen</Button>
              <Button onClick={() => { setShowPreview(false); handleSend(); }} className="btn-primary flex-1" disabled={sending} data-testid="send-from-preview">
                <Send className="w-4 h-4 mr-2" /> Jetzt senden
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ======================== SUPPORT TAB ========================
const SupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTickets = useCallback(async () => {
    try { const res = await adminAPI.getTickets(); setTickets(res.data || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = async (id) => {
    try { const res = await adminAPI.getTicket(id); setSelectedTicket(res.data); }
    catch (err) { console.error(err); }
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await adminAPI.replyTicket(selectedTicket.id, reply);
      setReply('');
      openTicket(selectedTicket.id);
      fetchTickets();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    await adminAPI.updateTicketStatus(selectedTicket.id, 'closed');
    openTicket(selectedTicket.id);
    fetchTickets();
  };

  const statusColors = { open: 'bg-amber-500/10 text-amber-400', answered: 'bg-blue-500/10 text-blue-400', closed: 'bg-slate-500/10 text-slate-400' };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="support-tab">
      <div className={`${selectedTicket ? 'lg:col-span-1' : 'lg:col-span-3'} glass-card p-6`}>
        <h2 className="text-lg font-semibold text-white mb-4">Tickets ({tickets.length})</h2>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div> : (
          <div className="space-y-2">
            {tickets.map(t => (
              <button key={t.id} onClick={() => openTicket(t.id)} className={`w-full text-left p-3 rounded-lg border transition-all ${selectedTicket?.id === t.id ? 'bg-[#EC2229]/10 border-[#EC2229]/30' : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'}`} data-testid={`ticket-row-${t.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-[#EC2229]">{t.ticket_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColors[t.status] || ''}`}>{t.status}</span>
                </div>
                <p className="text-sm text-white truncate">{t.subject}</p>
                <p className="text-xs text-slate-500">{t.user_name}</p>
              </button>
            ))}
            {tickets.length === 0 && <p className="text-slate-500 text-center py-8">Keine Tickets</p>}
          </div>
        )}
      </div>

      {selectedTicket && (
        <div className="lg:col-span-2 glass-card p-6" data-testid="ticket-detail">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">{selectedTicket.subject}</h3>
              <p className="text-sm text-slate-400">{selectedTicket.ticket_number} - {selectedTicket.user_name}</p>
            </div>
            {selectedTicket.status !== 'closed' && (
              <Button size="sm" variant="outline" onClick={handleCloseTicket} className="btn-secondary" data-testid="close-ticket">Schließen</Button>
            )}
            <button onClick={async () => { if(confirm('Ticket wirklich löschen?')) { await adminAPI.deleteTicket(selectedTicket.id); setSelectedTicket(null); fetchTickets(); } }} className="text-slate-600 hover:text-red-400 transition-colors ml-2" data-testid="delete-ticket-btn"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto mb-4">
            {selectedTicket.messages?.map(m => (
              <div key={m.id} className={`p-3 rounded-lg ${m.is_staff ? 'bg-[#EC2229]/10 border border-[#EC2229]/20 ml-8' : 'bg-white/5 mr-8'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-white">{m.user_name}</span>
                  {m.is_staff && <span className="text-[10px] bg-[#EC2229]/20 text-[#EC2229] px-1.5 py-0.5 rounded-full">Staff</span>}
                  <span className="text-[10px] text-slate-500 ml-auto">{new Date(m.created_at).toLocaleString('de-DE')}</span>
                </div>
                <p className="text-sm text-slate-300">{m.message}</p>
              </div>
            ))}
          </div>
          {selectedTicket.status !== 'closed' && (
            <div className="flex gap-2">
              <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Antwort..." className="input-field flex-1" data-testid="ticket-reply-input" />
              <Button onClick={handleReply} disabled={sending || !reply.trim()} className="btn-primary self-end" data-testid="send-ticket-reply">Antworten</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ======================== PRICING TAB ========================
const PricingTab = () => {
  const [packages, setPackages] = useState([]);
  const [tuningOptions, setTuningOptions] = useState([]);
  const [creditPrice, setCreditPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreatePkg, setShowCreatePkg] = useState(false);
  const [pkgForm, setPkgForm] = useState({ name: '', credits: '', price_eur: '', bonus_credits: '0', sort_order: '0' });

  const fetchData = useCallback(async () => {
    try {
      const [pkgRes, optRes, priceRes] = await Promise.all([
        adminAPI.getCreditPackages(),
        adminAPI.getTuningOptions(),
        adminAPI.getCreditPrice(),
      ]);
      setPackages(pkgRes.data || []);
      setTuningOptions(optRes.data || []);
      setCreditPrice(priceRes.data?.credit_price_eur || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreatePackage = async () => {
    try {
      await adminAPI.createCreditPackage({
        name: pkgForm.name,
        credits: parseFloat(pkgForm.credits),
        price_eur: parseFloat(pkgForm.price_eur),
        bonus_credits: parseFloat(pkgForm.bonus_credits) || 0,
        sort_order: parseInt(pkgForm.sort_order) || 0,
      });
      setShowCreatePkg(false);
      setPkgForm({ name: '', credits: '', price_eur: '', bonus_credits: '0', sort_order: '0' });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleDeletePackage = async (id) => {
    if (!confirm('Paket löschen?')) return;
    await adminAPI.deleteCreditPackage(id);
    fetchData();
  };

  const handleUpdateTuningOption = async (id, data) => {
    await adminAPI.updateTuningOption(id, data);
    fetchData();
  };

  const handleCreateTuningOption = async (data) => {
    await adminAPI.createTuningOption(data);
    fetchData();
  };

  const handleDeleteTuningOption = async (id) => {
    if (!confirm('Tuning-Option löschen?')) return;
    await adminAPI.deleteTuningOption(id);
    fetchData();
  };

  const handleUpdateCreditPrice = async () => {
    await adminAPI.updateCreditPrice(creditPrice);
    alert('Credit-Preis aktualisiert');
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>;

  return (
    <div className="space-y-6" data-testid="pricing-tab">
      {/* Credit Price */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#EC2229]" /> Credit-Preis</h2>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <Label className="label-text">1 Credit = EUR</Label>
            <Input type="number" step="0.01" value={creditPrice} onChange={e => setCreditPrice(parseFloat(e.target.value))} className="input-field" data-testid="credit-price-input" />
          </div>
          <Button onClick={handleUpdateCreditPrice} className="btn-primary" data-testid="save-credit-price">Speichern</Button>
        </div>
      </div>

      {/* Credit Packages */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#EC2229]" /> Credit-Pakete</h2>
          <Button onClick={() => setShowCreatePkg(true)} className="btn-primary" data-testid="create-package-btn"><CreditCard className="w-4 h-4 mr-2" /> Neues Paket</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {packages.map(p => (
            <div key={p.id} className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.06]" data-testid={`package-${p.id}`}>
              <div className="flex justify-between items-start">
                <h4 className="text-white font-medium">{p.name}</h4>
                <button onClick={() => handleDeletePackage(p.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
              <p className="text-2xl font-bold text-[#EC2229] mt-2">{p.credits} Cr</p>
              <p className="text-sm text-slate-400">{p.price_eur?.toFixed(2)} EUR</p>
              {p.bonus_credits > 0 && <p className="text-xs text-emerald-400 mt-1">+{p.bonus_credits} Bonus</p>}
            </div>
          ))}
        </div>

        {showCreatePkg && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="glass-card p-6 w-full max-w-md mx-4" data-testid="create-package-modal">
              <h3 className="text-lg font-semibold text-white mb-4">Neues Credit-Paket</h3>
              <div className="space-y-3">
                <Input value={pkgForm.name} onChange={e => setPkgForm(p => ({ ...p, name: e.target.value }))} placeholder="Name" className="input-field" />
                <Input type="number" value={pkgForm.credits} onChange={e => setPkgForm(p => ({ ...p, credits: e.target.value }))} placeholder="Credits" className="input-field" />
                <Input type="number" step="0.01" value={pkgForm.price_eur} onChange={e => setPkgForm(p => ({ ...p, price_eur: e.target.value }))} placeholder="Preis EUR" className="input-field" />
                <Input type="number" value={pkgForm.bonus_credits} onChange={e => setPkgForm(p => ({ ...p, bonus_credits: e.target.value }))} placeholder="Bonus Credits" className="input-field" />
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowCreatePkg(false)} className="btn-secondary flex-1">Abbrechen</Button>
                <Button onClick={handleCreatePackage} className="btn-primary flex-1" data-testid="save-package">Erstellen</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tuning Options */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Zap className="w-5 h-5 text-[#EC2229]" /> Tuning-Optionen Preise</h2>
          <Button onClick={() => handleCreateTuningOption({ name: 'Neue Option', credits: 1, category: 'custom' })} className="btn-primary text-xs" data-testid="add-tuning-option"><Zap className="w-3 h-3 mr-1" /> Option hinzufügen</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/[0.06]">
              <th className="text-left py-2 px-3 text-xs text-slate-500">Name</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Kategorie</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Credits</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">EUR</th>
              <th className="text-left py-2 px-3 text-xs text-slate-500">Aktion</th>
            </tr></thead>
            <tbody>
              {tuningOptions.map(opt => (
                <TuningOptionRow key={opt.id} opt={opt} creditPrice={creditPrice} onUpdate={handleUpdateTuningOption} onDelete={handleDeleteTuningOption} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TuningOptionRow = ({ opt, creditPrice, onUpdate, onDelete }) => {
  const [credits, setCredits] = useState(opt.credits);
  const [name, setName] = useState(opt.name);
  const [editing, setEditing] = useState(false);
  return (
    <tr className="border-b border-white/[0.03]" data-testid={`tuning-opt-${opt.id}`}>
      <td className="py-2 px-3">
        {editing ? (
          <Input value={name} onChange={e => setName(e.target.value)} className="input-field w-48 text-xs" data-testid={`tuning-name-${opt.id}`} />
        ) : (
          <span className="text-white">{opt.name}</span>
        )}
      </td>
      <td className="py-2 px-3 text-slate-400 text-xs">{opt.category}</td>
      <td className="py-2 px-3">
        {editing ? (
          <Input type="number" step="0.5" value={credits} onChange={e => setCredits(parseFloat(e.target.value))} className="input-field w-20 text-xs" data-testid={`tuning-credits-${opt.id}`} />
        ) : (
          <span className="text-white font-mono">{opt.credits}</span>
        )}
      </td>
      <td className="py-2 px-3 text-slate-400 font-mono text-xs">{(opt.credits * creditPrice).toFixed(2)}</td>
      <td className="py-2 px-3">
        {editing ? (
          <div className="flex gap-1">
            <button onClick={() => { onUpdate(opt.id, { credits, name }); setEditing(false); }} className="text-emerald-400 hover:text-emerald-300" data-testid={`save-tuning-${opt.id}`}><Check className="w-4 h-4" /></button>
            <button onClick={() => { setCredits(opt.credits); setName(opt.name); setEditing(false); }} className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} className="text-slate-500 hover:text-white" data-testid={`edit-tuning-${opt.id}`}><Edit className="w-4 h-4" /></button>
            {opt.is_custom && <button onClick={() => onDelete(opt.id)} className="text-slate-600 hover:text-red-400" data-testid={`delete-tuning-${opt.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        )}
      </td>
    </tr>
  );
};

// ======================== ANALYTICS / QM TAB (B7) ========================
const AnalyticsTab = () => {
  const [overview, setOverview] = useState(null);
  const [tunerData, setTunerData] = useState([]);
  const [ecuData, setEcuData] = useState([]);
  const [optionData, setOptionData] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ov, td, ed, od, bd, wd] = await Promise.all([
          adminAPI.getAnalyticsOverview(),
          adminAPI.getReclamationByTuner(),
          adminAPI.getReclamationByEcu(),
          adminAPI.getReclamationByOption(),
          adminAPI.getRevenueByBrand(),
          adminAPI.getAnalyticsWarnings(),
        ]);
        setOverview(ov.data);
        setTunerData(td.data);
        setEcuData(ed.data);
        setOptionData(od.data);
        setBrandData(bd.data);
        setWarnings(wd.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-2">
          {warnings.map((w, i) => (
            <div key={i} className={`p-3 rounded-lg flex items-start gap-3 text-sm ${
              w.severity === 'high' ? 'bg-red-500/10 border border-red-500/20' :
              w.severity === 'medium' ? 'bg-amber-500/10 border border-amber-500/20' :
              'bg-blue-500/10 border border-blue-500/20'
            }`} data-testid={`warning-${i}`}>
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                w.severity === 'high' ? 'text-red-400' : w.severity === 'medium' ? 'text-amber-400' : 'text-blue-400'
              }`} />
              <span className={w.severity === 'high' ? 'text-red-300' : w.severity === 'medium' ? 'text-amber-300' : 'text-blue-300'}>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI Overview Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Dateien gesamt', value: overview.total_files, icon: Files, color: 'text-blue-400' },
            { label: 'Abgeschlossen', value: overview.completed, icon: Check, color: 'text-emerald-400' },
            { label: 'In Bearbeitung', value: overview.in_progress, icon: Clock, color: 'text-amber-400' },
            { label: 'Reklamationen', value: overview.total_reclamations, icon: AlertTriangle, color: overview.total_reclamations > 0 ? 'text-red-400' : 'text-slate-400' },
            { label: 'Offen', value: overview.open_reclamations, icon: AlertTriangle, color: overview.open_reclamations > 0 ? 'text-red-400' : 'text-emerald-400' },
            { label: 'Kunden', value: overview.total_users, icon: Users, color: 'text-blue-400' },
          ].map((kpi, i) => (
            <div key={i} className="glass-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <kpi.icon className={`w-3.5 h-3.5 ${kpi.color}`} />
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">{kpi.label}</span>
              </div>
              <p className="text-2xl font-heading font-bold text-white">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Key Rates */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Reklamationsrate</p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-heading font-bold ${overview.reclamation_rate > 5 ? 'text-red-400' : overview.reclamation_rate > 2 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {overview.reclamation_rate}%
              </span>
              {overview.reclamation_rate > 5 ? <TrendingUp className="w-4 h-4 text-red-400" /> : <TrendingDown className="w-4 h-4 text-emerald-400" />}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{overview.total_reclamations} von {overview.total_files} Dateien</p>
          </div>
          <div className="glass-card p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">First-Time-Right</p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-heading font-bold ${overview.first_time_right_rate >= 90 ? 'text-emerald-400' : overview.first_time_right_rate >= 75 ? 'text-amber-400' : 'text-red-400'}`}>
                {overview.first_time_right_rate}%
              </span>
              <Activity className="w-4 h-4 text-slate-500" />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Dateien ohne Reklamation abgeschlossen</p>
          </div>
          <div className="glass-card p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Bearbeitungszeit</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-heading font-bold text-white">
                {overview.avg_processing_hours > 0 ? (overview.avg_processing_hours < 24 ? `${overview.avg_processing_hours}h` : `${(overview.avg_processing_hours / 24).toFixed(1)}d`) : 'N/A'}
              </span>
              <Clock className="w-4 h-4 text-slate-500" />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Durchschnittlich bis Abschluss</p>
          </div>
        </div>
      )}

      {/* Detailed Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Reclamation by Tuner */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#EC2229]" /> Reklamationsrate pro Tuner
          </h3>
          {tunerData.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Daten</p>
          ) : (
            <div className="space-y-2">
              {tunerData.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]" data-testid={`tuner-stat-${i}`}>
                  <div>
                    <p className="text-sm text-white font-medium">{t.tuner_name}</p>
                    <p className="text-[10px] text-slate-500">{t.total_files} Dateien, {t.completed_files} abgeschl.</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold font-mono ${t.reclamation_rate > 5 ? 'text-red-400' : t.reclamation_rate > 2 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {t.reclamation_rate}%
                    </span>
                    <p className="text-[10px] text-slate-500">{t.reclamations} Rekl.</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reclamation by ECU */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#EC2229]" /> Reklamationsrate pro ECU
          </h3>
          {ecuData.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Daten</p>
          ) : (
            <div className="space-y-2">
              {ecuData.map((e, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]" data-testid={`ecu-stat-${i}`}>
                  <div>
                    <p className="text-sm text-white font-medium font-mono">{e.ecu_type}</p>
                    <p className="text-[10px] text-slate-500">{e.total_files} Dateien</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold font-mono ${e.reclamation_rate > 5 ? 'text-red-400' : e.reclamation_rate > 2 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {e.reclamation_rate}%
                    </span>
                    <p className="text-[10px] text-slate-500">{e.reclamations} Rekl.</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reclamation by Tuning Option */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4 text-[#EC2229]" /> Reklamationsrate pro Option
          </h3>
          {optionData.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Daten</p>
          ) : (
            <div className="space-y-2">
              {optionData.map((o, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]" data-testid={`option-stat-${i}`}>
                  <div>
                    <p className="text-sm text-white font-medium">{o.option}</p>
                    <p className="text-[10px] text-slate-500">{o.total_files} Dateien</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold font-mono ${o.reclamation_rate > 8 ? 'text-red-400' : o.reclamation_rate > 3 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {o.reclamation_rate}%
                    </span>
                    <p className="text-[10px] text-slate-500">{o.reclamations} Rekl.</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revenue by Brand */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#EC2229]" /> Dateien pro Marke
          </h3>
          {brandData.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Daten</p>
          ) : (
            <div className="space-y-2">
              {brandData.map((b, i) => {
                const maxCount = brandData[0]?.count || 1;
                return (
                  <div key={i} className="flex items-center gap-3" data-testid={`brand-stat-${i}`}>
                    <span className="text-sm text-white font-medium w-24 truncate">{b.brand}</span>
                    <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#EC2229]/40 rounded-full flex items-center px-2" style={{ width: `${Math.max((b.count / maxCount) * 100, 8)}%` }}>
                        <span className="text-[10px] text-white font-mono">{b.count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ======================== SCRAPER TAB ========================
const ScraperTab = () => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, runsRes] = await Promise.all([adminAPI.getScraperStatus(), adminAPI.getScraperRuns()]);
      setStatus(statusRes.data);
      setRuns(runsRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const interval = setInterval(fetchData, 10000); return () => clearInterval(interval); }, [fetchData]);

  const handleRunScraper = async (sources) => {
    setRunning(true);
    try { await adminAPI.runScraper(sources); }
    catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setTimeout(() => { setRunning(false); fetchData(); }, 3000); }
  };

  const handleUploadCSV = async () => {
    if (!csvFile) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadCSV(csvFile);
      alert(res.data.message);
      setCsvFile(null);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>;

  return (
    <div className="space-y-6" data-testid="scraper-tab">
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Database className="w-5 h-5 text-blue-400" /> Fahrzeug-Datenbank Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/[0.03] rounded-lg p-4 text-center border border-white/[0.06]">
            <div className="text-2xl font-bold text-white">{status?.total_scraped?.toLocaleString() || 0}</div>
            <div className="text-xs text-slate-500">Gesamt</div>
          </div>
          {Object.entries(status?.by_source || {}).map(([src, cnt]) => (
            <div key={src} className="bg-white/[0.03] rounded-lg p-4 text-center border border-white/[0.06]">
              <div className="text-2xl font-bold text-white">{cnt?.toLocaleString()}</div>
              <div className="text-xs text-slate-500 capitalize">{src}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => handleRunScraper(['all'])} disabled={running || status?.is_running} className="btn-primary" data-testid="run-all-scraper">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Alle Scraper starten
          </Button>
          <Button onClick={() => handleRunScraper(['alientech'])} disabled={running} variant="outline" className="btn-secondary">Alientech</Button>
          <Button onClick={() => handleRunScraper(['autotuner'])} disabled={running} variant="outline" className="btn-secondary">Autotuner</Button>
        </div>
      </div>

      {/* CSV Upload */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Upload className="w-5 h-5 text-emerald-400" /> CSV Import (MagicMotorsport)</h2>
        <div className="flex gap-3 items-end">
          <Input type="file" accept=".csv" onChange={e => setCsvFile(e.target.files?.[0])} className="input-field flex-1" data-testid="csv-upload-input" />
          <Button onClick={handleUploadCSV} disabled={!csvFile || uploading} className="btn-primary" data-testid="upload-csv-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importieren
          </Button>
        </div>
      </div>

      {/* Recent Runs */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Letzte Scraper-Läufe</h2>
        <div className="space-y-2">
          {runs.slice(0, 10).map(r => (
            <div key={r.id} className="bg-white/[0.03] rounded-lg p-3 border border-white/[0.06] flex items-center justify-between">
              <div>
                <p className="text-sm text-white">{r.total_count?.toLocaleString()} Einträge</p>
                <p className="text-xs text-slate-500">{new Date(r.completed_at).toLocaleString('de-DE')}</p>
              </div>
              <span className="text-xs text-slate-400">{r.triggered_by}</span>
            </div>
          ))}
          {runs.length === 0 && <p className="text-slate-500 text-center py-4">Keine Läufe</p>}
        </div>
      </div>
    </div>
  );
};

// ======================== ACCOUNTING TAB (fs-patch-v3) ========================
const AccountingTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({ sum_gross: 0, sum_net: 0, sum_vat: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.q = search;
      const res = await adminAPI.listInvoices(params);
      setInvoices(res.data.items || []);
      setTotals(res.data.totals || {});
      setTotalPages(Math.max(1, Math.ceil((res.data.total || 0) / 50)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleCancel = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} wirklich stornieren?\n(Buchhaltungskonform: wird NICHT gelöscht, nur als storniert markiert.)`)) return;
    try {
      await adminAPI.cancelInvoice(inv.id);
      fetchInvoices();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleDownloadPdf = (inv) => {
    const token = localStorage.getItem('trxl_token');
    // fetch with auth header, then trigger download
    fetch(adminAPI.invoicePdfUrl(inv.id), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${inv.invoice_number}.pdf`;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
      })
      .catch(err => alert('PDF-Download fehlgeschlagen: ' + err.message));
  };

  const runBackfill = async (dry) => {
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const res = await adminAPI.runBackfill(dry);
      setBackfillResult(res.data);
      if (!dry) fetchInvoices();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setBackfillLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="accounting-tab">
      {/* Header + Totals */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><FileText className="w-5 h-5 text-[#EC2229]" /> Buchhaltung — Rechnungen ({totals.count || 0})</h2>
          <div className="flex gap-2 items-center">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Rechnungs-Nr., Name, E-Mail..." className="input-field pl-10" data-testid="invoice-search" />
            </div>
            <Button variant="outline" onClick={() => setShowBackfill(true)} className="btn-secondary flex items-center gap-2" data-testid="open-backfill-btn">
              <RefreshCw className="w-4 h-4" /> Backfill
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/[0.03] p-3 rounded-lg border border-white/[0.06]">
            <p className="text-xs text-slate-500">Anzahl</p>
            <p className="text-xl font-bold text-white" data-testid="totals-count">{totals.count || 0}</p>
          </div>
          <div className="bg-white/[0.03] p-3 rounded-lg border border-white/[0.06]">
            <p className="text-xs text-slate-500">Netto</p>
            <p className="text-xl font-bold text-white">{(totals.sum_net || 0).toFixed(2)} €</p>
          </div>
          <div className="bg-white/[0.03] p-3 rounded-lg border border-white/[0.06]">
            <p className="text-xs text-slate-500">MwSt.</p>
            <p className="text-xl font-bold text-white">{(totals.sum_vat || 0).toFixed(2)} €</p>
          </div>
          <div className="bg-[#EC2229]/10 p-3 rounded-lg border border-[#EC2229]/30">
            <p className="text-xs text-slate-400">Brutto (gesamt)</p>
            <p className="text-xl font-bold text-[#EC2229]" data-testid="totals-gross">{(totals.sum_gross || 0).toFixed(2)} €</p>
          </div>
        </div>
      </div>

      {/* Invoice list */}
      <div className="glass-card p-6">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>
        ) : invoices.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Keine Rechnungen{search ? ' für diese Suche' : ''} gefunden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Nummer</th>
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Datum</th>
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Kunde</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500">Netto</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500">MwSt.</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500">Brutto</th>
                  <th className="text-center py-2 px-3 text-xs text-slate-500">Status</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors" data-testid={`invoice-row-${inv.invoice_number}`}>
                    <td className="py-2.5 px-3 text-[#EC2229] font-mono text-xs">{inv.invoice_number}</td>
                    <td className="py-2.5 px-3 text-slate-400 text-xs">{(inv.created_at || '').substring(0, 10)}</td>
                    <td className="py-2.5 px-3 text-white">
                      <div className="flex flex-col">
                        <span>{inv.recipient?.name || '—'}</span>
                        <span className="text-xs text-slate-500">{inv.recipient?.email || ''}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-300">{(inv.amount || 0).toFixed(2)} €</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-400">{(inv.vat_amount || 0).toFixed(2)} €</td>
                    <td className="py-2.5 px-3 text-right font-mono text-white font-semibold">{(inv.total || 0).toFixed(2)} €</td>
                    <td className="py-2.5 px-3 text-center">
                      {inv.status === 'cancelled' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Storniert</span>
                      ) : inv.status === 'paid' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">Bezahlt</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Offen</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleDownloadPdf(inv)} className="text-slate-500 hover:text-[#EC2229] p-1" title="PDF herunterladen" data-testid={`pdf-btn-${inv.invoice_number}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        {inv.status !== 'cancelled' && (
                          <button onClick={() => handleCancel(inv)} className="text-slate-500 hover:text-red-400 p-1" title="Stornieren" data-testid={`cancel-btn-${inv.invoice_number}`}>
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-4">
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Zurück</Button>
            <span className="text-xs text-slate-400">Seite {page} / {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Weiter</Button>
          </div>
        )}
      </div>

      {/* Backfill Modal */}
      {showBackfill && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="backfill-modal">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-cyan-400" /> Backfill — Credits & Rechnungen</h3>
            <p className="text-sm text-slate-400 mb-4">
              Prüft alle abgeschlossenen Top-ups, korrigiert falsche Credit-Beträge (idempotent) und erzeugt fehlende Rechnungen.
              Starte ZUERST einen <span className="text-cyan-400 font-medium">Dry-Run</span>.
            </p>

            <div className="flex gap-3 mb-4">
              <Button onClick={() => runBackfill(true)} disabled={backfillLoading} className="btn-secondary flex-1" data-testid="backfill-dry-run">
                {backfillLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dry-Run (nur Preview)'}
              </Button>
              <Button onClick={() => runBackfill(false)} disabled={backfillLoading || !backfillResult || backfillResult?.dry_run !== true} className="btn-primary flex-1" data-testid="backfill-apply">
                {backfillLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply (scharf)'}
              </Button>
            </div>

            {backfillResult && (
              <div className="bg-black/30 rounded-lg p-4 border border-white/[0.06]">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${backfillResult.dry_run ? 'bg-cyan-500/10 text-cyan-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                    {backfillResult.dry_run ? 'DRY-RUN' : 'APPLIED'}
                  </span>
                  <span className="text-xs text-slate-400">
                    Inspiziert: {backfillResult.summary?.inspected || 0} · Balance-Korrekturen: {backfillResult.summary?.balance_fixed || 0} · Rechnungen: {backfillResult.summary?.invoices_created || 0} neu / {backfillResult.summary?.invoices_existing || 0} bestehend
                  </span>
                </div>

                <div className="max-h-80 overflow-y-auto space-y-2">
                  {(backfillResult.items || []).map((it, idx) => (
                    <div key={idx} className="text-xs bg-white/[0.03] rounded p-2 border border-white/[0.04]">
                      <div className="flex justify-between items-start gap-2 mb-1">
                        <span className="text-white font-medium">{it.user_name || it.user_id?.substring(0, 8)}</span>
                        <span className="text-slate-400">{it.amount_eur?.toFixed(2)} €</span>
                      </div>
                      <div className="text-slate-500">
                        Paket: {it.package || '—'} · Gutgeschrieben: {it.credited?.toFixed(0)} · Korrekt: {it.correct?.toFixed(0)} · Diff: <span className={it.diff > 0.01 ? 'text-amber-400' : it.diff < -0.01 ? 'text-red-400' : 'text-emerald-400'}>{it.diff > 0 ? '+' : ''}{it.diff?.toFixed(0)}</span>
                      </div>
                      <div className="text-[10px] text-cyan-400 mt-1">{(it.actions || []).join(' · ')}</div>
                    </div>
                  ))}
                  {(!backfillResult.items || backfillResult.items.length === 0) && (
                    <p className="text-slate-500 text-center py-4">Keine Transaktionen gefunden.</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => { setShowBackfill(false); setBackfillResult(null); }} className="btn-secondary">Schließen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ======================== CREDIT HISTORY TAB (fs-patch-v4) ========================
const CreditHistoryTab = () => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_in: 0, total_out: 0, net: 0, count: 0 });
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [sinceFilter, setSinceFilter] = useState('');
  const [untilFilter, setUntilFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers({ limit: 500 });
      setUsers(res.data || []);
    } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.q = search;
      if (typeFilter) params.tx_type = typeFilter;
      if (userFilter) params.user_id = userFilter;
      if (sinceFilter) params.since = sinceFilter;
      if (untilFilter) params.until = untilFilter;
      const res = await adminAPI.walletHistory(params);
      setItems(res.data.items || []);
      setTotals(res.data.totals || {});
      setTypes(res.data.types || []);
      setTotalPages(Math.max(1, Math.ceil((res.data.total || 0) / 50)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, search, typeFilter, userFilter, sinceFilter, untilFilter]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const typeColors = {
    credit: 'bg-emerald-500/10 text-emerald-400',
    debit: 'bg-red-500/10 text-red-400',
    file_purchase: 'bg-red-500/10 text-red-400',
    admin_credit: 'bg-blue-500/10 text-blue-400',
    admin_debit: 'bg-orange-500/10 text-orange-400',
    admin_adjustment: 'bg-cyan-500/10 text-cyan-400',
    backfill_correction: 'bg-amber-500/10 text-amber-400',
    bonus: 'bg-purple-500/10 text-purple-400',
  };

  const typeLabels = {
    credit: 'Aufladung',
    debit: 'Abbuchung',
    file_purchase: 'Datei-Kauf',
    admin_credit: 'Admin +',
    admin_debit: 'Admin -',
    admin_adjustment: 'Korrektur',
    backfill_correction: 'Backfill',
    bonus: 'Bonus',
  };

  const resetFilters = () => {
    setSearch(''); setTypeFilter(''); setUserFilter('');
    setSinceFilter(''); setUntilFilter(''); setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="credit-history-tab">
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#EC2229]" /> Credit-Verlauf
            <span className="text-sm text-slate-500 font-normal">({totals.count || 0} Einträge)</span>
          </h2>
          <div className="flex gap-2 items-center flex-wrap">
            <Button size="sm" variant="outline" onClick={resetFilters} className="btn-secondary" data-testid="reset-filters-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Filter zurücksetzen
            </Button>
          </div>
        </div>

        {/* Filters row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Beschreibung / Referenz..." className="input-field pl-10" data-testid="history-search" />
          </div>
          <select
            value={userFilter}
            onChange={e => { setUserFilter(e.target.value); setPage(1); }}
            className="input-field bg-black/30 text-white"
            data-testid="history-user-filter"
          >
            <option value="">Alle Benutzer</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
            className="input-field bg-black/30 text-white"
            data-testid="history-type-filter"
          >
            <option value="">Alle Typen</option>
            {types.map(t => (
              <option key={t.type} value={t.type}>{typeLabels[t.type] || t.type} ({t.count})</option>
            ))}
          </select>
          <Input type="date" value={sinceFilter} onChange={e => { setSinceFilter(e.target.value); setPage(1); }} className="input-field" data-testid="history-since" placeholder="Von" />
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/[0.03] p-3 rounded-lg border border-white/[0.06]">
            <p className="text-xs text-slate-500">Einträge</p>
            <p className="text-xl font-bold text-white" data-testid="history-count">{totals.count || 0}</p>
          </div>
          <div className="bg-emerald-500/10 p-3 rounded-lg border border-emerald-500/30">
            <p className="text-xs text-emerald-400/70">Summe rein</p>
            <p className="text-xl font-bold text-emerald-400">+{(totals.total_in || 0).toFixed(1)} Cr</p>
          </div>
          <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/30">
            <p className="text-xs text-red-400/70">Summe raus</p>
            <p className="text-xl font-bold text-red-400">{(totals.total_out || 0).toFixed(1)} Cr</p>
          </div>
          <div className="bg-[#EC2229]/10 p-3 rounded-lg border border-[#EC2229]/30">
            <p className="text-xs text-slate-400">Netto-Saldo</p>
            <p className={`text-xl font-bold ${(totals.net || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{(totals.net || 0) >= 0 ? '+' : ''}{(totals.net || 0).toFixed(1)} Cr</p>
          </div>
        </div>
      </div>

      {/* History table */}
      <div className="glass-card p-6">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>
        ) : items.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Keine Transaktionen gefunden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Datum</th>
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Kunde</th>
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Typ</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500">Betrag</th>
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Beschreibung</th>
                  <th className="text-left py-2 px-3 text-xs text-slate-500">Referenz</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.id || idx} className="border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors" data-testid={`history-row-${idx}`}>
                    <td className="py-2.5 px-3 text-slate-500 text-xs font-mono whitespace-nowrap">{(it.created_at || '').substring(0, 16).replace('T', ' ')}</td>
                    <td className="py-2.5 px-3 text-white">
                      <div className="flex flex-col">
                        <span>{it.user_name || '—'}</span>
                        <span className="text-xs text-slate-500">{it.user_email || ''}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[it.type] || 'bg-slate-500/10 text-slate-400'}`}>
                        {typeLabels[it.type] || it.type}
                      </span>
                    </td>
                    <td className={`py-2.5 px-3 text-right font-mono font-bold whitespace-nowrap ${(it.amount || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(it.amount || 0) >= 0 ? '+' : ''}{(it.amount || 0).toFixed(1)}
                    </td>
                    <td className="py-2.5 px-3 text-slate-300 text-xs max-w-md truncate" title={it.description}>{it.description}</td>
                    <td className="py-2.5 px-3 text-slate-500 text-xs font-mono truncate max-w-[160px]" title={it.reference}>{it.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-4">
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Zurück</Button>
            <span className="text-xs text-slate-400">Seite {page} / {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Weiter</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
